import React from 'react';
import { FileText, Download, Eye, Search, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { documents } from '../mock/mockData';
import { toast } from '../hooks/use-toast';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const statusColor = (s) => {
  if (s === 'Paid' || s === 'Signed' || s === 'Active' || s === 'Generated') return 'bg-[#00A651]/10 text-[#00A651]';
  if (s === 'Pending') return 'bg-[#FFB800]/15 text-[#B37800]';
  return 'bg-gray-100 text-gray-600';
};

export default function Documents() {
  const [tab, setTab] = React.useState('all');
  const filter = (t) => {
    if (t === 'all') return documents;
    if (t === 'elr') return documents.filter(d => d.type.includes('e-LR'));
    if (t === 'ewb') return documents.filter(d => d.type.includes('e-Way'));
    if (t === 'invoice') return documents.filter(d => d.type === 'Invoice');
    if (t === 'pod') return documents.filter(d => d.type.includes('PoD'));
    return documents;
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">Documents</h1>
          <p className="text-sm text-gray-500 mt-1">GST-compliant e-LR, e-Way Bills, Invoices & PoD.</p>
        </div>
        <Button className="bg-[#00A651] hover:bg-[#008a43] text-white" onClick={() => toast({ title: 'Generate document', description: 'Select a load from My Loads to generate documents.' })}><Plus className="w-4 h-4 mr-2" />Generate Document</Button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search by load ID, LR number, invoice..." className="pl-9" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-100 flex-wrap h-auto">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="elr">e-LR</TabsTrigger>
          <TabsTrigger value="ewb">e-Way Bill</TabsTrigger>
          <TabsTrigger value="invoice">Invoices</TabsTrigger>
          <TabsTrigger value="pod">PoD</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <div className="grid gap-3">
            {filter(tab).map(d => (
              <div key={d.id} className="bg-white rounded-2xl border border-gray-100 p-4 hover-lift flex flex-wrap items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0B2545]/5 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#0B2545]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-[#0B2545]">{d.type}</p>
                    <Badge className={`border-0 text-xs ${statusColor(d.status)}`}>{d.status}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Load {d.loadId} • {d.from} → {d.to} • {d.date}</p>
                  {d.ewbNumber && <p className="text-xs text-gray-500 font-mono">EWB: {d.ewbNumber}</p>}
                  {d.invoiceNumber && <p className="text-xs text-gray-500 font-mono">{d.invoiceNumber}</p>}
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#0B2545]">{formatINR(d.amount)}</p>
                  <p className="text-xs text-gray-500">Freight Value</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toast({ title: 'Opening preview', description: d.id })}><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" className="bg-[#0B2545] hover:bg-[#13315C] text-white" onClick={() => {
                    if (d.type === 'Invoice') {
                      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/loads/${d.loadId}/invoice/pdf`, '_blank');
                      toast({ title: 'Downloading invoice', description: `GST invoice for ${d.loadId}` });
                    } else {
                      toast({ title: 'Downloading', description: `${d.type} for ${d.loadId}` });
                    }
                  }}><Download className="w-4 h-4 mr-1" />PDF</Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
